import {
  AuthAcceptInvite,
  AuthForgotPassword,
  AuthMfaSetup,
  AuthMfaVerify,
  AuthRecovery,
  AuthResetPassword,
  parseResetQuery,
} from '@aka/ui'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { i18n } from '@/i18n'
import { useAuthStore } from '@/stores/auth'
import { useBrandStore } from '@/stores/brand'
import { resolveNavigation, type RouteMeta as ConsoleRouteMeta } from './guards'

declare module 'vue-router' {
  interface RouteMeta extends ConsoleRouteMeta {
    /** Catalogue key for the page title and the shell heading. */
    titleKey?: string
  }
}

// Every console view is lazy: the sign-in page should not carry the panel
// with it. The pre-console pages are the library's own components, already in
// the main chunk, wired through the AuthFlow context App.vue provides.
const routes: RouteRecordRaw[] = [
  {
    path: '/sign-in',
    name: 'sign-in',
    component: () => import('@/views/SignInView.vue'),
    meta: { public: true, titleKey: 'signIn.title' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: AuthForgotPassword,
    meta: { public: true, titleKey: 'password.forgotTitle' },
  },
  {
    // The API emails a link to this path with token and email in the query.
    path: '/reset-password',
    name: 'reset-password',
    component: AuthResetPassword,
    props: (route) => ({ link: parseResetQuery(route.query) }),
    meta: { public: true, titleKey: 'password.resetTitle' },
  },
  {
    // The API emails an invitation link to this path with token and email in the query.
    path: '/accept-invite',
    name: 'accept-invite',
    component: AuthAcceptInvite,
    props: (route) => ({ link: parseResetQuery(route.query) }),
    meta: { public: true, titleKey: 'invite.title' },
  },
  {
    path: '/mfa/recovery',
    name: 'mfa-recovery',
    component: AuthRecovery,
    meta: { stage: 'mfa', titleKey: 'mfa.recoverySignInTitle' },
  },
  {
    path: '/mfa/verify',
    name: 'mfa-verify',
    component: AuthMfaVerify,
    meta: { stage: 'mfa', titleKey: 'mfa.verifyTitle' },
  },
  {
    path: '/mfa/setup',
    name: 'mfa-setup',
    component: AuthMfaSetup,
    meta: { stage: 'mfa', titleKey: 'mfa.setupTitle' },
  },
  {
    path: '/',
    component: () => import('@/layouts/OperatorLayout.vue'),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { titleKey: 'dashboard.title' },
      },
      {
        path: 'tenants',
        name: 'tenants',
        component: () => import('@/views/TenantsView.vue'),
        meta: { titleKey: 'tenants.title', permission: 'tenants.read' },
      },
      {
        path: 'tenants/:id',
        name: 'tenant',
        component: () => import('@/views/TenantView.vue'),
        meta: { titleKey: 'tenants.detailTitle', permission: 'tenants.read' },
      },
      {
        path: 'tenants/:id/offboarding',
        name: 'tenant-offboarding',
        component: () => import('@/views/TenantOffboardingView.vue'),
        meta: { titleKey: 'offboarding.title', permission: 'tenants.read' },
      },
      {
        path: 'tenants/:id/billing',
        name: 'tenant-billing',
        component: () => import('@/views/TenantBillingView.vue'),
        meta: { titleKey: 'billing.tenantTitle', permission: 'billing.read' },
      },
      {
        path: 'billing',
        name: 'billing',
        component: () => import('@/views/BillingView.vue'),
        meta: { titleKey: 'billing.title', permission: 'billing.read' },
      },
      {
        path: 'plans',
        name: 'plans',
        component: () => import('@/views/PlansView.vue'),
        meta: { titleKey: 'plans.title', permission: 'billing.read' },
      },
      {
        path: 'plans/:id',
        name: 'plan',
        component: () => import('@/views/PlanView.vue'),
        meta: { titleKey: 'plans.detailTitle', permission: 'billing.read' },
      },
      {
        path: 'health',
        name: 'health',
        component: () => import('@/views/HealthView.vue'),
        meta: { titleKey: 'health.title', permission: 'amapi.health' },
      },
      {
        path: 'health/dead-letters',
        name: 'health-dead-letters',
        component: () => import('@/views/DeadLettersView.vue'),
        meta: { titleKey: 'health.deadLettersTitle', permission: 'amapi.health' },
      },
      {
        path: 'publishing',
        name: 'publishing',
        component: () => import('@/views/PublishingView.vue'),
        meta: { titleKey: 'publishing.title', permission: 'apps.publish' },
      },
      {
        path: 'publishing/:id',
        name: 'published-app',
        component: () => import('@/views/PublishedAppView.vue'),
        meta: { titleKey: 'publishing.detailTitle', permission: 'apps.publish' },
      },
      {
        path: 'audit',
        name: 'audit',
        component: () => import('@/views/AuditView.vue'),
        meta: { titleKey: 'audit.title', permission: 'audit.read' },
      },
      {
        path: 'settings',
        component: () => import('@/views/SettingsView.vue'),
        // Both operator roles hold users.read today; a role without it would
        // need the redirect to land on notifications instead.
        redirect: { name: 'settings-operators' },
        children: [
          {
            path: 'operators',
            name: 'settings-operators',
            component: () => import('@/views/settings/OperatorsPanel.vue'),
            meta: { titleKey: 'operators.title', permission: 'users.read' },
          },
          {
            // Preferences are the caller's own; a session is all the API asks for.
            path: 'notifications',
            name: 'settings-notifications',
            component: () => import('@/views/settings/NotificationsPanel.vue'),
            meta: { titleKey: 'notifications.title' },
          },
        ],
      },
      // The devices commands without a shell (saas-admin#53). The flag is
      // read here as the raw expression, not through the store's constant:
      // Vite folds it to a literal in this module, so a build without the
      // flag drops the branch and the view's chunk with it. Through an import
      // the chunk survives (scripts/check-advanced-absent.mjs holds the line).
      ...(import.meta.env.VITE_ADVANCED_TOOLS === 'true'
        ? [
            {
              path: 'advanced',
              name: 'advanced',
              component: () => import('@/views/AdvancedView.vue'),
              meta: { titleKey: 'advanced.title', permission: 'tenants.manage' },
            },
          ]
        : []),
      {
        // The signed-in operator's own account: every role has one, so no permission.
        path: 'account',
        name: 'account',
        component: () => import('@/views/AccountView.vue'),
        meta: { titleKey: 'account.title' },
      },
      {
        // The technical pages, for every operator. One route for the first
        // page and for every page under it.
        path: 'help/:slug(.*)*',
        name: 'help',
        component: () => import('@/views/HelpView.vue'),
        meta: { titleKey: 'help.title' },
      },
      {
        path: 'forbidden',
        name: 'forbidden',
        component: () => import('@/views/ForbiddenView.vue'),
        meta: { titleKey: 'error.forbiddenTitle' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true, titleKey: 'error.notFoundTitle' },
  },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  // One re-validation per page load, before the first route resolves, so no
  // guard ever decides on a session it has not checked.
  if (!auth.ready) await auth.restore()

  return resolveNavigation(
    { name: String(to.name ?? ''), fullPath: to.fullPath, meta: to.meta },
    {
      isAuthenticated: auth.isAuthenticated,
      mfaStage: auth.mfaStage,
      can: auth.can,
    },
  )
})

router.afterEach((to) => {
  const key = to.meta.titleKey
  if (typeof key !== 'string') return
  // Tab title only. The brand half comes from the API, so on a cold load the
  // first title is the section alone and gains the product name a moment later.
  useBrandStore().setPageTitle(i18n.global.t(key))
})
